import {
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useDepartments,
  useRemoveDepartment,
  useSaveDepartment,
} from '@/api/hooks'
import type { DepartmentView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

const BLANK: DepartmentView = {
  id: '',
  name: '',
  sortOrder: 99,
  active: true,
}

/**
 * The parts of the business something can be given away out of.
 *
 * <p>A complimentary bill has to name one before it can be issued, and the
 * complimentary register totals a year by it. That is the whole reason this is
 * a list and not a box somebody types into: "Mktg", "Marketing" and
 * "marketing" are three departments to a report and one to everybody who works
 * here.</p>
 */
export function DepartmentsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const target = companyId ?? ownCompanyId
  const { data: companies } = useCompanies()
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1

  const { data: departments, error } = useDepartments({ companyId, includeInactive: true })
  const save = useSaveDepartment()
  const remove = useRemoveDepartment()

  const [rows, setRows] = useState<DepartmentView[]>([])
  const writable = canWrite('config')

  useEffect(() => {
    setRows((departments ?? []).map((row) => ({ ...row })))
  }, [departments])

  const update = (index: number, patch: Partial<DepartmentView>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? remove.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {canPickCompany ? (
            <TextField
              select
              size="small"
              label="Company"
              value={target ?? ''}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Company' }}
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Whose budget a complimentary bill comes out of. The complimentary register totals the
            year by these.
          </Typography>
        </Stack>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((current) => [...current, { ...BLANK }])}
          >
            New department
          </Button>
        ) : null}
      </Stack>

      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell width={320}>Department</TableCell>
            <TableCell width={110}>Order</TableCell>
            <TableCell width={90}>Offered</TableCell>
            <TableCell align="right" width={120}>
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id || `new-${index}`} sx={{ opacity: row.active ? 1 : 0.55 }}>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={row.name}
                  disabled={!writable}
                  onChange={(event) => update(index, { name: event.target.value })}
                  inputProps={{ 'aria-label': `Department ${index + 1}` }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={row.sortOrder}
                  disabled={!writable}
                  onChange={(event) => update(index, { sortOrder: Number(event.target.value) })}
                  inputProps={{ 'aria-label': `Order ${index + 1}` }}
                />
              </TableCell>
              <TableCell>
                {/* Withdrawn rather than deleted: a bill given away under it
                    three years ago still names it, and has to go on reading
                    the same. */}
                <Tooltip title="Off means it is no longer offered; bills already given away under it still name it">
                  <Switch
                    size="small"
                    checked={row.active}
                    disabled={!writable}
                    onChange={(event) => update(index, { active: event.target.checked })}
                    inputProps={{ 'aria-label': `Offered ${index + 1}` }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                {writable ? (
                  <>
                    <Button
                      size="small"
                      disabled={save.isPending || !row.name.trim()}
                      onClick={() =>
                        save.mutate({
                          id: row.id || undefined,
                          companyId,
                          name: row.name,
                          sortOrder: row.sortOrder,
                          active: row.active,
                        })
                      }
                    >
                      Save
                    </Button>
                    {row.id ? (
                      <Tooltip title="Withdraw">
                        <IconButton
                          size="small"
                          onClick={() => remove.mutate({ id: row.id, companyId })}
                          aria-label={`Withdraw ${row.name}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}
